import { Stack, Table, Text } from "@mantine/core";

import type { WeekGroup } from "./macro-aggregations";
import { MACRO_FIELDS, type MacroTargetValues } from "./macro-aggregations";
import { MacroTargetCell } from "./MacroTargetCell";

const compactTableStyle = { fontSize: "0.8125rem" } as const;
const macroCellStyle = { verticalAlign: "top", minWidth: 170 } as const;

const createMacroHeaderCells = (prefix: string) =>
  MACRO_FIELDS.map((field) => (
    <Table.Th key={`${prefix}-${field.key}`} style={{ textAlign: "left" }}>
      {field.label}
    </Table.Th>
  ));

const createMacroValueCells = (
  totals: WeekGroup["totals"],
  prefix: string,
  macroTargets?: MacroTargetValues,
  multiplier = 7,
) =>
  MACRO_FIELDS.map((field) => {
    const dailyTarget = macroTargets?.[field.key] ?? null;
    const targetValue = dailyTarget === null || typeof dailyTarget !== "number" ? null : dailyTarget * multiplier;

    return (
      <Table.Td key={`${prefix}-${field.key}`} style={macroCellStyle}>
        <MacroTargetCell actualValue={totals[field.key]} targetValue={targetValue} targetLabel="weekly target" />
      </Table.Td>
    );
  });

const createWeeklyHeaderRow = () => (
  <Table.Tr>
    <Table.Th>Week</Table.Th>
    <Table.Th>Days logged</Table.Th>
    <Table.Th>Meals logged</Table.Th>
    {createMacroHeaderCells("weekly-header")}
  </Table.Tr>
);

const createWeeklyBodyRows = (weekGroups: WeekGroup[], macroTargets?: MacroTargetValues, multiplier = 7) =>
  weekGroups.map((week) => (
    <Table.Tr key={week.key}>
      <Table.Td>
        <Text fw={600}>{week.label}</Text>
      </Table.Td>
      <Table.Td>{week.dayCount}</Table.Td>
      <Table.Td>{week.mealCount}</Table.Td>
      {createMacroValueCells(week.totals, `${week.key}-weekly`, macroTargets, multiplier)}
    </Table.Tr>
  ));

type WeeklyBreakdownTableProps = {
  weekGroups: WeekGroup[];
  macroTargets?: MacroTargetValues;
  targetMultiplier?: number;
};

export const WeeklyBreakdownTable = ({ weekGroups, macroTargets, targetMultiplier = 7 }: WeeklyBreakdownTableProps) => {
  if (weekGroups.length === 0) {
    return null;
  }

  const headerRow = createWeeklyHeaderRow();
  const bodyRows = createWeeklyBodyRows(weekGroups, macroTargets, targetMultiplier);

  return (
    <Stack gap="xs">
      <div style={{ overflowX: "auto", border: "1px solid #E5E7EB", borderRadius: 12 }}>
        <Table
          horizontalSpacing="sm"
          verticalSpacing="xs"
          striped
          highlightOnHover
          style={compactTableStyle}
        >
          <Table.Thead>{headerRow}</Table.Thead>
          <Table.Tbody>{bodyRows}</Table.Tbody>
        </Table>
      </div>
    </Stack>
  );
};
